import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Inventory2 } from '@mui/icons-material';
import { SectionPaper, SectionHeader } from '../../../common/components/styled-ui-elements';
import type { ItemMasterFacilityFreshnessView, ItemMasterRunStatus } from '../../../claims';

interface ItemMasterStatusCardProps {
  /** Undefined until a mirror run has begun for this facility. */
  facility?: ItemMasterFacilityFreshnessView;
}

const toLocalDateTime = (isoUtc: string | null): string =>
  isoUtc ? new Date(isoUtc).toLocaleString().replace(',', '') : '—';

const STATUS_COLOR: Record<ItemMasterRunStatus, 'success' | 'error' | 'warning'> = {
  Successful: 'success',
  Failed: 'error',
  ExtractionFailed: 'error',
  Started: 'warning',
};

/**
 * Read-only item-master freshness status card (CAM-C10 US3). Presentational: the tab owns the query
 * so both panels share one loading and error state. Visual chrome copied from `AppConfigSection`'s
 * `SectionPaper`/`SectionHeader` (not its editable-field behavior).
 */
export const ItemMasterStatusCard: React.FC<ItemMasterStatusCardProps> = ({ facility }) => {
  return (
    <SectionPaper>
      <SectionHeader icon={<Inventory2 sx={{ mr: 1, fontSize: 20 }} />} title="Item Master Status" />

      {!facility ? (
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          No item-master data available for this facility
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Total Stored Records
            </Typography>
            <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600 }}>
              {facility.totalStoredRecordCount.toLocaleString()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Watermark
            </Typography>
            <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600 }}>
              {toLocalDateTime(facility.watermark)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Last Run At
            </Typography>
            <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600 }}>
              {toLocalDateTime(facility.lastRunAtUtc)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Status
            </Typography>
            {facility.status ? (
              <Chip label={facility.status} color={STATUS_COLOR[facility.status]} size="small" />
            ) : (
              <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600 }}>
                —
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </SectionPaper>
  );
};
